import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import OpsChatFirstTimeModal from "./OpsChatFirstTimeModal";
import userModel from "../lib/firebase/userModel";
import unitModel from "../lib/firebase/unitModel";
import { hasLeoKnowledgeBaseContext } from "../helpers/opsChatSetup";
import { noFirstTimeModalPaths } from "../helpers/constants";

/**
 * Shows OpsChatFirstTimeModal for OpsChat-only subscribers on first login.
 * Mirrors the logic of FirstTimeUserModalWrapper but for the OpsChat onboarding
 * checklist (add door → knowledge base → share chat link).
 *
 * Firestore flag: hasSeenOpsChatFirstTimeModal
 * Session flag:   opsChatFirstTimeModalShown_{uid}
 */
const OpsChatFirstTimeModalWrapper = () => {
  const { user, userInfo, isUserInfoLoading, isOpsChatOnlyUser } =
    useContext(AuthContext);
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const hasCheckedRef = useRef(null);

  const shouldExcludeModal = noFirstTimeModalPaths.some((path) => {
    if (path === "/") return location.pathname === "/";
    if (path.includes(":")) {
      return location.pathname.startsWith(path.split(":")[0]);
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  });

  useEffect(() => {
    // Only run for OpsChat users
    if (!isOpsChatOnlyUser) return;
    if (shouldExcludeModal) return;
    if (isUserInfoLoading) return;
    if (!user) {
      hasCheckedRef.current = null;
      return;
    }

    // Only check once per user session while modal is closed
    if (hasCheckedRef.current === user.uid && !showModal) return;

    const hasSeenInFirebase = userInfo?.hasSeenOpsChatFirstTimeModal === true;
    const sessionKey = `opsChatFirstTimeModalShown_${user.uid}`;
    const hasSeenInSession = sessionStorage.getItem(sessionKey) === "true";

    hasCheckedRef.current = user.uid;

    let timeoutId = null;

    const checkAndMaybeShow = async () => {
      try {
        const units = await unitModel.getByOwner(user.uid);
        const hasUnit = units && units.length > 0;
        const hasKnowledgeBase = hasLeoKnowledgeBaseContext(userInfo, units);

        const allDone = hasUnit && hasKnowledgeBase;

        if (allDone) {
          setShowModal(false);
          sessionStorage.setItem(sessionKey, "true");
          if (!hasSeenInFirebase && user?.uid) {
            await userModel.update({
              id: user.uid,
              data: { hasSeenOpsChatFirstTimeModal: true },
            });
          }
          return;
        }

        if (!hasSeenInFirebase && !hasSeenInSession) {
          timeoutId = setTimeout(() => {
            setShowModal(true);
            sessionStorage.setItem(sessionKey, "true");
          }, 1000);
        }
      } catch (err) {
        console.error("[OpsChatFirstTimeModalWrapper] Error checking:", err);
        if (!hasSeenInFirebase && !hasSeenInSession) {
          timeoutId = setTimeout(() => {
            setShowModal(true);
            sessionStorage.setItem(sessionKey, "true");
          }, 1000);
        }
      }
    };

    checkAndMaybeShow();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    user,
    userInfo,
    isUserInfoLoading,
    isOpsChatOnlyUser,
    shouldExcludeModal,
    location.pathname,
    showModal,
  ]);

  const handleClose = () => setShowModal(false);

  if (!isOpsChatOnlyUser) return null;

  return <OpsChatFirstTimeModal isOpen={showModal} onClose={handleClose} />;
};

export default OpsChatFirstTimeModalWrapper;
